import { useLayoutEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/lib/auth';
import {
  userNeedsRegistrationGate,
} from '@/lib/registrationApproval';
import { userNeedsSupplierOnboarding } from '@/lib/supplierOnboarding';
import { Loading } from '@/components/Loading';
import { SupplierOnboardingRedirect } from '@/components/SupplierOnboardingRedirect';

/**
 * Redirects users whose account registration is pending or rejected to /waiting-list.
 * Approved users on /waiting-list are sent to the main app (or supplier onboarding).
 */
export function RegistrationApprovalRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoading } = useAuth({ tmp: 'RegistrationApprovalRedirect' });
  const path = location.pathname;

  const skipGate = path === '/design-system';
  const needsGate = userNeedsRegistrationGate(user);
  const isWaitingList = path === '/waiting-list';

  useLayoutEffect(() => {
    if (skipGate || isLoading) return;

    if (!user) {
      if (isWaitingList) {
        navigate('/auth/login', { replace: true });
      }
      return;
    }

    if (needsGate && !isWaitingList) {
      navigate('/waiting-list', { replace: true });
      return;
    }

    if (!needsGate && isWaitingList) {
      navigate(userNeedsSupplierOnboarding(user) ? '/onboarding' : '/', { replace: true });
    }
  }, [skipGate, isLoading, user, needsGate, isWaitingList, path, navigate]);

  const blockOutlet =
    !skipGate && !isLoading && !!user && needsGate && !isWaitingList;

  if (blockOutlet) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (isWaitingList || skipGate) {
    return <Outlet />;
  }

  return <SupplierOnboardingRedirect />;
}
